import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Inbox, RefreshCw, Upload, Check, X, Scissors, ExternalLink, Loader2, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';
import DatePickerField from '@/components/DatePickerField';

const TIPI = [
  { value: 'referto_laboratorio', label: 'Referto di laboratorio' },
  { value: 'referto_specialistico', label: 'Referto specialistico' },
  { value: 'radiologia', label: 'Radiologia / imaging' },
  { value: 'ecg', label: 'ECG' },
  { value: 'lettera_dimissione', label: 'Lettera di dimissione' },
  { value: 'verbale_pronto_soccorso', label: 'Verbale di pronto soccorso' },
  { value: 'documento_amministrativo', label: 'Documento amministrativo' },
  { value: 'altro', label: 'Altro' },
];

const confidenzaBadge = {
  alta: 'bg-emerald-100 text-emerald-700',
  media: 'bg-amber-100 text-amber-700',
  nessuna: 'bg-slate-100 text-slate-600',
};

const statoBadge = {
  da_smistare: 'bg-amber-100 text-amber-700',
  assegnato: 'bg-emerald-100 text-emerald-700',
  scartato: 'bg-slate-100 text-slate-500',
  diviso: 'bg-blue-100 text-blue-700',
};

export default function DocumentiDaSmistare() {
  const { hasRole } = useAuth();
  const token = localStorage.getItem('rsa_token');
  const fileRef = useRef(null);
  const [documenti, setDocumenti] = useState([]);
  const [ospiti, setOspiti] = useState([]);
  const [stato, setStato] = useState('da_smistare');
  const [loading, setLoading] = useState(true);
  const [controllo, setControllo] = useState(false);
  const [caricamento, setCaricamento] = useState(false);
  const [info, setInfo] = useState(null);
  const [selezionato, setSelezionato] = useState(null);
  const [form, setForm] = useState({ ospite_id: '', tipo_documento: 'altro', data_documento: '', titolo: '' });
  const [cerca, setCerca] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);
  const [scartoMotivo, setScartoMotivo] = useState('');
  const [parti, setParti] = useState([{ da_pagina: 1, a_pagina: 1 }]);
  const [showDividi, setShowDividi] = useState(false);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const [docs, conteggio] = await Promise.all([
        api.get('/acquisizione/documenti', { params: { stato } }),
        api.get('/acquisizione/conteggio'),
      ]);
      setDocumenti(docs.data);
      setInfo(conteggio.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore caricamento documenti'); }
    setLoading(false);
  }, [stato]);

  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { api.get('/ospiti', { params: { stato: 'presente' } }).then(r => setOspiti(r.data)).catch(() => {}); }, []);

  const controllaAdesso = async () => {
    setControllo(true);
    try {
      const r = await api.post('/acquisizione/controlla');
      if (r.data.eseguito === false) toast.info('Acquisizione non attiva: configurala in Impostazioni');
      else if (!r.data.ok) toast.error(r.data.errore || 'Casella non raggiungibile');
      else toast.success(`${r.data.documenti} documenti letti, ${r.data.assegnati_auto} assegnati automaticamente`);
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore controllo casella'); }
    setControllo(false);
  };

  const caricaFile = async (files) => {
    if (!files?.length) return;
    setCaricamento(true);
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('files', f));
    try {
      const r = await api.post('/acquisizione/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.documenti.length} documenti letti`);
      (r.data.errori || []).forEach(e => toast.error(`${e.file}: ${e.motivo}`));
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore caricamento'); }
    setCaricamento(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const apriDettaglio = (d) => {
    setSelezionato(d);
    setForm({
      ospite_id: d.ospite_proposto || '',
      tipo_documento: d.tipo_documento || 'altro',
      data_documento: d.data_documento || new Date().toISOString().slice(0, 10),
      titolo: d.titolo || '',
    });
    setScartoMotivo('');
    setParti([{ da_pagina: 1, a_pagina: d.pagine || 1 }]);
    setCerca('');
  };

  const assegna = async () => {
    if (!form.ospite_id) { toast.error('Seleziona l\'ospite'); return; }
    setSalvataggio(true);
    try {
      await api.put(`/acquisizione/${selezionato.id}/assegna`, form);
      toast.success('Documento archiviato nella cartella clinica');
      setSelezionato(null);
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore assegnazione'); }
    setSalvataggio(false);
  };

  const scarta = async () => {
    if (!scartoMotivo.trim()) { toast.error('Indica il motivo dello scarto'); return; }
    setSalvataggio(true);
    try {
      await api.put(`/acquisizione/${selezionato.id}/scarta`, { motivo: scartoMotivo });
      toast.success('Documento scartato');
      setSelezionato(null);
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setSalvataggio(false);
  };

  const dividi = async () => {
    setSalvataggio(true);
    try {
      const r = await api.post(`/acquisizione/${selezionato.id}/dividi`, {
        parti: parti.map(p => ({ da_pagina: Number(p.da_pagina), a_pagina: Number(p.a_pagina) })),
      });
      toast.success(r.data.message);
      setShowDividi(false);
      setSelezionato(null);
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore divisione'); }
    setSalvataggio(false);
  };

  const fileUrl = (d) => `${process.env.REACT_APP_BACKEND_URL}/api/acquisizione/${d.id}/file?auth=${token}`;
  const ospitiFiltrati = ospiti.filter(o => !cerca || `${o.cognome} ${o.nome}`.toLowerCase().includes(cerca.toLowerCase()));

  return (
    <div className="space-y-5 max-w-[1400px]" data-testid="smistamento-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] flex items-center gap-2">
            <Inbox className="w-6 h-6 text-brand" />Documenti da smistare
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Referti arrivati dalla casella dedicata e in attesa di attribuzione all'ospite
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
            onChange={e => caricaFile(e.target.files)} data-testid="upload-input" />
          <Button variant="outline" size="sm" className="h-9" onClick={() => fileRef.current?.click()}
            disabled={caricamento} data-testid="upload-btn">
            {caricamento ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Carica file
          </Button>
          <Button size="sm" className="h-9 bg-brand hover:bg-brand-dark text-white"
            onClick={controllaAdesso} disabled={controllo} data-testid="controlla-adesso-btn">
            {controllo ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Controllo...</> : <><RefreshCw className="w-4 h-4 mr-1" />Controlla adesso</>}
          </Button>
        </div>
      </div>

      {info && (
        <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500">
          <Badge className="bg-amber-100 text-amber-700" data-testid="conteggio-da-smistare">{info.da_smistare} da smistare</Badge>
          {!info.attiva && <span className="flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3.5 h-3.5" />Casella non attiva: configurala in Impostazioni</span>}
          {info.ultimo_controllo && (
            <span>
              Ultimo controllo {new Date(info.ultimo_controllo.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}
              {info.ultimo_controllo.ok === false && <span className="text-red-600"> - {info.ultimo_controllo.errore}</span>}
            </span>
          )}
          <Select value={stato} onValueChange={setStato}>
            <SelectTrigger className="h-8 w-44 text-xs ml-auto" data-testid="filtro-stato"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="da_smistare">Da smistare</SelectItem>
              <SelectItem value="assegnato">Assegnati</SelectItem>
              <SelectItem value="scartato">Scartati</SelectItem>
              <SelectItem value="diviso">Divisi</SelectItem>
              <SelectItem value="tutti">Tutti</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="h-16 p-3" /></Card>)}</div>
      ) : documenti.length === 0 ? (
        <div className="text-center py-16">
          <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Nessun documento in questo stato</p>
        </div>
      ) : (
        <div className="space-y-2">
          {documenti.map(d => (
            <Card key={d.id} className="hover:shadow-sm transition-shadow cursor-pointer"
              onClick={() => apriDettaglio(d)} data-testid={`doc-${d.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-slate-500 uppercase">
                  {(d.original_filename || '').split('.').pop()?.slice(0, 4)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-800 truncate">{d.titolo || d.original_filename}</p>
                    <Badge className={`text-[10px] ${statoBadge[d.stato] || statoBadge.scartato}`}>{d.stato.replace('_', ' ')}</Badge>
                    <Badge variant="outline" className="text-[10px]">{d.tipo_label}</Badge>
                    {d.piu_documenti && <Badge className="bg-blue-100 text-blue-700 text-[10px]">possibili piu documenti</Badge>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5 flex-wrap">
                    <span>{d.data_documento}</span>
                    {d.paziente_letto && <span>letto: <strong className="text-slate-600">{d.paziente_letto}</strong></span>}
                    {d.ospite_nome && <span className="text-emerald-600">{d.ospite_nome}</span>}
                    {d.struttura && <span>{d.struttura}</span>}
                    <span>{d.pagine} pag.</span>
                    <span>{d.origine === 'email' ? `posta: ${d.mittente}` : 'caricamento manuale'}</span>
                  </div>
                </div>
                <Badge className={`text-[10px] flex-shrink-0 ${confidenzaBadge[d.match_confidenza] || confidenzaBadge.nessuna}`}>
                  {d.match_motivo}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selezionato} onOpenChange={o => !o && setSelezionato(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          {selezionato && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base">
                  <Inbox className="w-5 h-5 text-brand" />{selezionato.titolo || selezionato.original_filename}
                </DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="border rounded-lg overflow-hidden bg-slate-50 h-[460px]">
                    {selezionato.content_type === 'application/pdf' ? (
                      <iframe title="anteprima" src={fileUrl(selezionato)} className="w-full h-full" />
                    ) : (
                      <img src={fileUrl(selezionato)} alt="anteprima" className="w-full h-full object-contain" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={fileUrl(selezionato)} target="_blank" rel="noreferrer" className="text-xs text-brand flex items-center gap-1" data-testid="apri-file-link">
                      <ExternalLink className="w-3.5 h-3.5" />Apri a schermo intero
                    </a>
                    {selezionato.content_type === 'application/pdf' && selezionato.pagine > 1 && selezionato.stato === 'da_smistare' && (
                      <Button variant="outline" size="sm" className="h-7 text-xs ml-auto" onClick={() => setShowDividi(true)} data-testid="dividi-btn">
                        <Scissors className="w-3.5 h-3.5 mr-1" />Dividi scansione
                      </Button>
                    )}
                  </div>
                  {selezionato.sintesi_ai && (
                    <p className="text-xs text-slate-500 bg-slate-50 rounded p-2 leading-relaxed">{selezionato.sintesi_ai}</p>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="text-xs bg-slate-50 rounded-lg p-3 space-y-1">
                    <p><span className="text-slate-400">Paziente letto:</span> <strong>{selezionato.paziente_letto || 'non leggibile'}</strong></p>
                    <p><span className="text-slate-400">Data di nascita letta:</span> {selezionato.data_nascita_letta || '-'}</p>
                    <p><span className="text-slate-400">Riconoscimento:</span> {selezionato.match_motivo}</p>
                    {selezionato.limiti_ai && <p className="text-amber-600">{selezionato.limiti_ai}</p>}
                  </div>

                  {selezionato.stato === 'da_smistare' ? (
                    <>
                      <div>
                        <Label className="text-xs">Ospite *</Label>
                        <div className="relative mt-1">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          <Input placeholder="Cerca ospite..." className="pl-8 h-8 text-xs" value={cerca}
                            onChange={e => setCerca(e.target.value)} data-testid="cerca-ospite" />
                        </div>
                        {(selezionato.alternative || []).length > 0 && !cerca && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {selezionato.alternative.map(a => (
                              <button key={a.id} onClick={() => setForm(f => ({ ...f, ospite_id: a.id }))}
                                data-testid={`alternativa-${a.id}`}
                                className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${form.ospite_id === a.id ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                                {a.nome}{a.stanza ? ` - st. ${a.stanza}` : ''}
                              </button>
                            ))}
                          </div>
                        )}
                        <Select value={form.ospite_id} onValueChange={v => setForm(f => ({ ...f, ospite_id: v }))}>
                          <SelectTrigger className="h-9 text-xs mt-2" data-testid="select-ospite"><SelectValue placeholder="Seleziona ospite" /></SelectTrigger>
                          <SelectContent className="max-h-72">
                            {ospitiFiltrati.map(o => (
                              <SelectItem key={o.id} value={o.id}>{o.cognome} {o.nome}{o.stanza ? ` - st. ${o.stanza}` : ''}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Tipo documento</Label>
                          <Select value={form.tipo_documento} onValueChange={v => setForm(f => ({ ...f, tipo_documento: v }))}>
                            <SelectTrigger className="h-9 text-xs" data-testid="select-tipo"><SelectValue /></SelectTrigger>
                            <SelectContent>{TIPI.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Data documento</Label>
                          <DatePickerField className="h-9 text-xs" value={form.data_documento} onChange={v => setForm(f => ({ ...f, data_documento: v }))} testId="input-data" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Titolo</Label>
                        <Input className="h-9 text-xs" value={form.titolo}
                          onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))} data-testid="input-titolo" />
                      </div>
                      <Button className="w-full bg-brand hover:bg-brand-dark text-white" onClick={assegna}
                        disabled={salvataggio} data-testid="assegna-btn">
                        <Check className="w-4 h-4 mr-1" />Assegna alla cartella
                      </Button>

                      {hasRole('coordinatore', 'medico', 'infermiere') && (
                        <div className="border-t pt-3 space-y-2">
                          <Label className="text-xs text-slate-500">Scarta il documento (pubblicita, pagina bianca)</Label>
                          <div className="flex gap-2">
                            <Input className="h-8 text-xs" placeholder="Motivo dello scarto" value={scartoMotivo}
                              onChange={e => setScartoMotivo(e.target.value)} data-testid="input-scarto" />
                            <Button variant="outline" size="sm" className="h-8 text-xs text-red-600 border-red-200"
                              onClick={scarta} disabled={salvataggio} data-testid="scarta-btn">
                              <X className="w-3.5 h-3.5 mr-1" />Scarta
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-xs text-slate-500 space-y-1 bg-emerald-50 rounded-lg p-3">
                      <p><strong>{selezionato.stato === 'assegnato' ? 'Assegnato' : selezionato.stato}</strong></p>
                      {selezionato.ospite_nome && <p>Ospite: {selezionato.ospite_nome}</p>}
                      {selezionato.assegnato_da && <p>Da {selezionato.assegnato_da} il {new Date(selezionato.assegnato_il).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}</p>}
                      {selezionato.scarto_motivo && <p>Motivo scarto: {selezionato.scarto_motivo}</p>}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showDividi} onOpenChange={setShowDividi}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><Scissors className="w-5 h-5 text-brand" />Dividi la scansione</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Indica le pagine di ogni documento contenuto nella scansione ({selezionato?.pagine} pagine in totale).
            </p>
            {parti.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <Label className="text-xs w-20">Doc. {i + 1}</Label>
                <Input type="number" min={1} max={selezionato?.pagine} className="h-8 text-xs w-20" value={p.da_pagina}
                  onChange={e => setParti(ps => ps.map((x, j) => j === i ? { ...x, da_pagina: e.target.value } : x))}
                  data-testid={`parte-da-${i}`} />
                <span className="text-xs text-slate-400">a</span>
                <Input type="number" min={1} max={selezionato?.pagine} className="h-8 text-xs w-20" value={p.a_pagina}
                  onChange={e => setParti(ps => ps.map((x, j) => j === i ? { ...x, a_pagina: e.target.value } : x))}
                  data-testid={`parte-a-${i}`} />
                {parti.length > 1 && (
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setParti(ps => ps.filter((_, j) => j !== i))}>
                    <X className="w-3.5 h-3.5 text-slate-400" />
                  </Button>
                )}
              </div>
            ))}
            <Button variant="outline" size="sm" className="h-8 text-xs"
              onClick={() => setParti(ps => [...ps, { da_pagina: 1, a_pagina: selezionato?.pagine || 1 }])} data-testid="aggiungi-parte">
              Aggiungi documento
            </Button>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDividi(false)}>Annulla</Button>
              <Button className="bg-brand hover:bg-brand-dark text-white" onClick={dividi} disabled={salvataggio} data-testid="conferma-dividi">
                {salvataggio ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Dividi
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
